/* eslint-disable sort-keys */
/* eslint-disable sort-keys-fix/sort-keys-fix */
import { buttonSizes } from "../components/Button";
import { getUserTheme } from "./userTheme";
import { Colors } from "./colors";
import { getThemeByName } from "./themes";
/**
 * For testing purposes. This should be overriden in application usage.
 */
const defaultMessagesThemeSettings = {
    beta: "Beta",
    trafficinsightsPreview: "PREVIEW",
};
const buildTheme = (userTheme, messagesTheme) => {
    const fonts = {
        bold: "OpenSans-Bold, Helvetica, Arial, Verdana, sans-serif",
        main: "'Open Sans', Helvetica, Arial, Verdana, sans-serif",
    };
    // Check where url comes from S3
    const isS3Logo = (url) => /https?:\/\/s3\.amazonaws\.com\/.*/.test(url);
    // Spacings
    const spacings = {
        detailsAreaPadding: "50px",
        mapControlButton: "30px",
        mapSearchWidth: "450px",
        navigationRadius: "25px",
        panelRadius: "4px",
        panelSpacing: "12px",
        panelSpacingHalf: "6px",
        panelSpacingOutside: "18px",
        responsiveNav: "1250px",
        responsiveRow: "1250px",
        sidebarFooterHeight: "60px",
        tableMinWidth1: "250px",
        tableMinWidth2: "250px",
        tableMinWidth3: "250px",
        tableMinWidth4: "340px",
        tableMinWidth5: "415px",
        tableMinWidth6: "500px",
        tableMinWidth7: "580px",
        tableMinWidth8: "670px",
        tableMinWidth9: "760px",
        tableMinWidth10: "840px",
        tableMinWidth11: "840px",
        tableMinWidth12: "840px",
    };
    // Used this chart as a starting point - https://stackoverflow.com/questions/7015302/css-hexadecimal-rgba/8254129#8254129
    // and tweaked some values as needed.
    const hexAlphaOpacities = {
        HEX_ALPHA_OPACITY_0: "00",
        HEX_ALPHA_OPACITY_5: "0D",
        HEX_ALPHA_OPACITY_10: "1A",
        HEX_ALPHA_OPACITY_14: "24",
        HEX_ALPHA_OPACITY_15: "26",
        HEX_ALPHA_OPACITY_20: "33",
        HEX_ALPHA_OPACITY_25: "40",
        HEX_ALPHA_OPACITY_29: "4A",
        HEX_ALPHA_OPACITY_30: "4D",
        HEX_ALPHA_OPACITY_35: "59",
        HEX_ALPHA_OPACITY_40: "66",
        HEX_ALPHA_OPACITY_43: "6E",
        HEX_ALPHA_OPACITY_45: "73",
        HEX_ALPHA_OPACITY_50: "80",
        HEX_ALPHA_OPACITY_55: "8C",
        HEX_ALPHA_OPACITY_57: "91",
        HEX_ALPHA_OPACITY_60: "99",
        HEX_ALPHA_OPACITY_65: "A6",
        HEX_ALPHA_OPACITY_70: "B3",
        HEX_ALPHA_OPACITY_71: "B5",
        HEX_ALPHA_OPACITY_75: "BF",
        HEX_ALPHA_OPACITY_80: "CC",
        HEX_ALPHA_OPACITY_85: "D9",
        HEX_ALPHA_OPACITY_86: "DB",
        HEX_ALPHA_OPACITY_90: "E6",
        HEX_ALPHA_OPACITY_95: "F2",
        HEX_ALPHA_OPACITY_100: "FF",
    };
    // Built from existing spacings in use
    const space = [0, 4, 8, 16, 32, 64, 128, 256];
    const sizes = [0, 4, 8, 16, 32, 64, 128, 256, 512, 1024];
    // Reduced font size list from existing fonts to enforce some consistency
    const fontSizes = [11, 13, 16, 18, 24, 35];
    const radii = ["0px", "5px", "25px"];
    const lineHeights = ["19px"];
    const borders = ["0px", "1px solid", "3px solid"];
    const shadows = {
        floatingBox: "rgba(0, 0, 0, 0.15) 0px 0px 9px 0px",
        listItem: `0px 1px 1px 0px ${Colors.lightGray[500]}`,
        listItemBottomShadow: `0 1px 0 0 ${Colors.lightGray[300]}, 0 -1px 0 0 ${Colors.lightGray[300]}`,
        modal: "0 3px 14px 1px rgba(0, 0, 0, 0.2)",
        searchBox: {
            contentShadow: "rgb(0 0 0 / 8%) 0px 1px 1px inset, rgb(102 175 233 / 60%) 0px 0px 8px",
            simpleShadow: "rgb(0 0 0 / 15%) 0px 1px 4px",
        },
        toastBox: "rgb(0 0 0 / 15%) -5px 5px 5px 0px",
    };
    const breakpointDefs = [
        {
            key: "xs",
            value: "640px",
        },
        {
            key: "sm",
            value: "832px",
        },
        {
            key: "md",
            value: "1024px",
        },
        {
            key: "lg",
            value: "1280px",
        },
        {
            key: "xl",
            value: "1920px",
        },
    ];
    const breakpoints = breakpointDefs.map((def) => def.value);
    breakpointDefs.forEach((def) => {
        breakpoints[def.key] = def.value;
    });
    const transition = "all 500ms";
    const transitions = {
        background: (enabled = false, { from = "white", to = "black" } = {}) => ({
            background: enabled ? from : to,
            transition,
        }),
        center: (enabled = false) => ({
            left: enabled ? "50%" : 0,
            position: "absolute",
            top: enabled ? "50%" : 0,
            transform: enabled ? "translate(-50%, -50%)" : null,
            transition,
        }),
        clipDown: (enabled = false) => ({
            clipPath: `polygon(0% ${enabled ? 100 : 0}%, 100% ${enabled ? 100 : 0}%, 100% 100%, 0% 100%)`,
            transition,
        }),
        clipLeft: (enabled = false) => ({
            clipPath: `polygon(0% 0%, ${enabled ? 0 : 100}% 0%, ${enabled ? 0 : 100}% 100%, 0 100%)`,
            transition,
        }),
        clipRight: (enabled = false) => ({
            clipPath: `polygon(${enabled ? 100 : 0}% 0%, 100% 0%, 100% 100%, ${enabled ? 100 : 0}% 100%)`,
            transition,
        }),
        clipUp: (enabled = false) => ({
            clipPath: `polygon(0% 0%, 100% 0%, 100% ${enabled ? 100 : 0}%, 0% ${enabled ? 100 : 0}%)`,
            transition,
        }),
        fade: (enabled = false, { from = 1, to = 0 } = {}) => ({ opacity: enabled ? to : from, transition }),
        fullscreen: (enabled = false) => ({
            minHeight: `${enabled ? 100 : 0}vh`,
            minWidth: `${enabled ? 100 : 0}vw`,
            position: enabled ? "fixed" : "inherit",
            transition,
        }),
        slideDown: (enabled = false) => ({
            transform: `scaleY(${enabled ? 0 : 1})`,
            transformOrigin: "bottom",
            transition,
        }),
        slideLeft: (enabled = false) => ({
            transform: `scaleX(${enabled ? 1 : 0})`,
            transformOrigin: "left",
            transition,
        }),
        slideRight: (enabled = false) => ({
            transform: `scaleX(${enabled ? 1 : 0})`,
            transformOrigin: "right",
            transition,
        }),
        slideUp: (enabled = false) => ({
            transform: `scaleY(${enabled ? 0 : 1})`,
            transformOrigin: "top",
            transition,
        }),
        zoom: (enabled = false, { from = 0, to = 1 } = {}) => ({
            transform: `scale(${enabled ? to : from})`,
            transition,
        }),
    };
    const theme = getThemeByName(userTheme);
    return Object.assign({ borders,
        breakpoints, buttons: {
            sizes: buttonSizes,
        }, colors: theme.colors, svg: theme.svg, fonts,
        fontSizes,
        hexAlphaOpacities,
        lineHeights, messages: messagesTheme, radii,
        shadows,
        sizes,
        space,
        transitions, 
        /** Legacy variables that are still used */
        // Custom logos are uploaded to S3 and are 75px high
        logoHeight: isS3Logo(userTheme.logoUrl) ? "75px" : "49px", logoUrl: userTheme.logoUrl, mainFontFamily: "'Open Sans', Helvetica, Arial, Verdana, sans-serif", tooltipRadius: "3px", zIndexModal: 5000 }, spacings);
};
const getUserDerivedTheme = (userTheme, messagesTheme) => buildTheme(userTheme, messagesTheme);
export const getTheme = (themeSettings, messagesTheme = defaultMessagesThemeSettings) => getUserDerivedTheme(getUserTheme(themeSettings), messagesTheme);
